/**
 * Walkthrough coach-mark overlay (ProjectTasks #109 Phase 1).
 *
 * Self-contained, no third-party tour library. When the active step names a
 * target selector that resolves to an on-screen element, the overlay draws a
 * spotlight cutout around it and anchors the instruction card to it. When the
 * selector is absent or the element isn't found, it falls back to a dimmed
 * backdrop with a centered card — so an unverified selector never breaks the
 * experience, it just degrades to a centered coach mark.
 *
 * Advance is manual in Phase 1 (the step's CTA button). Route navigation
 * happens when a step activates if the step declares a `route`.
 */

import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight, X, Pause, Check, MousePointerClick, Eye, Flag, Radar, GripVertical, Minus, LifeBuoy, BookOpen, Sparkles } from 'lucide-react'
import { useWalkthroughStore } from '@/modules/walkthrough/walkthroughStore'
import { snapshotPersonaForStep } from '@/modules/walkthrough/stepDetection'
import { useAppStore } from '@/store/appStore'
import { cn } from '@/lib/utils'

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

const PAD = 8 // spotlight padding around the target element

export function WalkthroughOverlay() {
  const activeScenarioId = useWalkthroughStore((s) => s.activeScenarioId)
  const stepIndex = useWalkthroughStore((s) => s.stepIndex)
  const paused = useWalkthroughStore((s) => s.paused)
  const next = useWalkthroughStore((s) => s.next)
  const prev = useWalkthroughStore((s) => s.prev)
  const recordResult = useWalkthroughStore((s) => s.recordResult)
  const pause = useWalkthroughStore((s) => s.pause)
  const exit = useWalkthroughStore((s) => s.exit)
  const currentStep = useWalkthroughStore((s) => s.currentStep)
  const activeScenario = useWalkthroughStore((s) => s.activeScenario)

  const navigate = useNavigate()
  const location = useLocation()
  const [targetRect, setTargetRect] = useState<Rect | null>(null)

  const step = currentStep()
  const scenario = activeScenario()
  const totalSteps = scenario?.steps.length ?? 0

  // Flag-note capture (test-plan runner): "Didn't match" opens a note box
  // instead of recording instantly, so the run report can quote what the
  // user actually saw. Reset per step.
  const [flagNoteOpen, setFlagNoteOpen] = useState(false)
  const [flagNote, setFlagNote] = useState('')

  // #109 SELF-SOLVE — a "Stuck?" panel the user can open any time they don't
  // understand a step, and which auto-opens when they click "Didn't match".
  // Hybrid: deterministic troubleshooting bullets + "open the full guide" +
  // "ask the assistant" (the in-context AI panel, seeded with step context).
  const [helpOpen, setHelpOpen] = useState(false)
  const openAiPanelWithPrompt = useAppStore((s) => s.openAiPanelWithPrompt)

  // Do-it-to-advance (smart steps): a step that declares verifyComplete gets
  // NO manual advance — we poll the predicate and auto-advance with a brief
  // success flash the moment the user's real action makes it true.
  const [detected, setDetected] = useState(false)
  const advancedRef = useRef(false)

  // MOVE IT / SHRINK IT (Connor, 2026-07-22: "make this movable so I can move
  // it out of the way, or a way to see and work on the screen fully"): the
  // card header is a drag handle (pointer capture, viewport-clamped), and a
  // minimize button collapses the card to a compact pill. Both persist across
  // steps + reloads so the card stays where you put it for the whole journey.
  const [cardPos, setCardPos] = useState<{ x: number; y: number } | null>(() => {
    try {
      const raw = localStorage.getItem('ppm-walkthrough-card-pos')
      return raw ? (JSON.parse(raw) as { x: number; y: number }) : null
    } catch { return null }
  })
  const [minimized, setMinimized] = useState<boolean>(() => {
    try { return localStorage.getItem('ppm-walkthrough-card-min') === '1' } catch { return false }
  })
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  const setMinimizedPersist = (v: boolean) => {
    setMinimized(v)
    try { localStorage.setItem('ppm-walkthrough-card-min', v ? '1' : '0') } catch { /* ignore */ }
  }

  const onDragStart = (e: React.PointerEvent) => {
    // Buttons in the header keep their own behavior.
    if ((e.target as HTMLElement).closest('button')) return
    const el = cardRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: r.left, origY: r.top }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    const w = cardRef.current?.offsetWidth ?? 360
    const h = cardRef.current?.offsetHeight ?? 200
    const x = Math.max(4, Math.min(d.origX + (e.clientX - d.startX), window.innerWidth - w - 4))
    const y = Math.max(4, Math.min(d.origY + (e.clientY - d.startY), window.innerHeight - Math.min(h, 120)))
    setCardPos({ x, y })
  }
  const onDragEnd = () => {
    if (dragRef.current && cardPos) {
      try { localStorage.setItem('ppm-walkthrough-card-pos', JSON.stringify(cardPos)) } catch { /* ignore */ }
    }
    dragRef.current = null
  }

  // Navigate to the step's route when the step changes (not on every render).
  useEffect(() => {
    if (!step?.route) return
    if (location.pathname !== step.route) {
      navigate(step.route)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenarioId, stepIndex])

  // Per-step activation housekeeping: snapshot the acting persona (feeds the
  // personaChanged() detection), reset the note box + detection state.
  useEffect(() => {
    snapshotPersonaForStep()
    setFlagNoteOpen(false)
    setFlagNote('')
    setHelpOpen(false)
    setDetected(false)
    advancedRef.current = false
  }, [activeScenarioId, stepIndex])

  // The detection loop. Cheap DOM/store predicates (~see stepDetection.ts),
  // polled at 700ms while the step is active and unpaused. On first true:
  // flash "Detected ✓" for a beat, then record a pass (test-plan steps) or
  // plain-advance. advancedRef guards double-fires across the timeout.
  useEffect(() => {
    const verify = step?.verifyComplete
    if (!activeScenarioId || paused || !verify) return
    const iv = window.setInterval(() => {
      if (advancedRef.current) return
      let ok = false
      try { ok = verify() } catch { ok = false }
      if (!ok) return
      advancedRef.current = true
      setDetected(true)
      window.setTimeout(() => {
        if (step?.expect) recordResult('pass')
        else next()
      }, 900)
    }, 700)
    return () => window.clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenarioId, stepIndex, paused])

  // When a step names a target, scroll it into view once after the route has
  // painted so a below-the-fold spotlight isn't off-screen. Deliberately a
  // one-shot per step (not in `measure`) so it doesn't fight the user's own
  // scrolling while they read the card.
  useEffect(() => {
    if (!activeScenarioId || paused || !step?.targetSelector) return
    const selector = step.targetSelector
    const t = setTimeout(() => {
      const el = document.querySelector(selector)
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 180)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenarioId, stepIndex, paused])

  // Resolve the target element's rect; recompute on scroll / resize. Falls
  // back to null (centered card) when no selector or no match.
  const measure = useCallback(() => {
    if (!step?.targetSelector) {
      setTargetRect(null)
      return
    }
    const el = document.querySelector(step.targetSelector)
    if (!el) {
      setTargetRect(null)
      return
    }
    const r = el.getBoundingClientRect()
    setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height })
  }, [step?.targetSelector])

  useLayoutEffect(() => {
    if (!activeScenarioId || paused) return
    // Defer one frame so a freshly-navigated route has painted its DOM.
    const raf = requestAnimationFrame(measure)
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [activeScenarioId, stepIndex, paused, measure])

  if (!activeScenarioId || paused || !step || !scenario) return null

  const isLastStep = stepIndex >= totalSteps - 1
  const placement = step.placement ?? 'center'

  // Spotlight cutout: a box positioned over the target with a massive
  // box-shadow that dims everything outside it.
  const spotlight = targetRect && {
    top: targetRect.top - PAD,
    left: targetRect.left - PAD,
    width: targetRect.width + PAD * 2,
    height: targetRect.height + PAD * 2,
  }

  // Card position. With a target + non-center placement, anchor near the
  // target; otherwise center it.
  let cardStyle: React.CSSProperties = {}
  if (spotlight && placement !== 'center') {
    const gap = 12
    if (placement === 'bottom') cardStyle = { top: spotlight.top + spotlight.height + gap, left: spotlight.left }
    else if (placement === 'top') cardStyle = { top: Math.max(8, spotlight.top - gap - 180), left: spotlight.left }
    else if (placement === 'right') cardStyle = { top: spotlight.top, left: spotlight.left + spotlight.width + gap }
    else if (placement === 'left') cardStyle = { top: spotlight.top, left: Math.max(8, spotlight.left - gap - 360) }
  }
  const centered = !spotlight || placement === 'center'

  // HANDS-ON steps must leave the page fully usable: a step that WATCHES for
  // the user's real action (verifyComplete), or asks them to try something and
  // judge the result (example/expect), can't sit under a click-blocking veil
  // with the card parked over the work area ("How am I supposed to fill out
  // the page when it is greyed out?" — Connor, 2026-07-22). For those steps:
  // no backdrop, container passes clicks through, card docks bottom-left out
  // of the grid's way. Pure read-then-advance steps keep the classic dim.
  const handsOn = Boolean(step.verifyComplete || step.example || step.expect)

  // #109 SELF-SOLVE handlers.
  const openGuide = () => {
    const q = encodeURIComponent(step.helpQuery ?? step.title)
    window.open(`/help?q=${q}`, '_blank', 'noopener')
  }
  const askAssistant = () => {
    const parts = [
      `I'm following the "${scenario.name}" guided walkthrough, on the step "${step.title}".`,
      `The instruction says: "${step.body}"`,
      step.example ? `It asks me to try: "${step.example}"` : '',
      step.expect ? `I should see: "${step.expect}"` : '',
      `I don't understand this step or it didn't work as described. Explain in plain terms what to do, where to click, and what might be going wrong.`,
    ].filter(Boolean)
    openAiPanelWithPrompt(parts.join(' '))
  }
  const troubleshootItems = step.troubleshoot && step.troubleshoot.length > 0
    ? step.troubleshoot
    : [
        'Make sure you are on the page and acting as the persona this step names (check the top bar).',
        step.example ? 'Re-read the "Try it" line and do exactly that action, then look again.' : 'Re-read the instruction and look for the element it names on the page.',
        'If the page looks stale, reload it, then resume the walkthrough (your place is saved).',
      ]

  return (
    <div
      className={cn('fixed inset-0 z-[60]', handsOn && 'pointer-events-none')}
      role="dialog"
      aria-modal={handsOn ? undefined : true}
      aria-label="Guided walkthrough"
      data-walkthrough="1"
    >
      {/* Backdrop + spotlight. When the step resolves a target we draw a ring
          with a box-shadow cutout that dims everything OUTSIDE it, narrowing
          focus to where the action is. The cutout div is pointer-events-none,
          so even on hands-on steps the page (and the spotlit control) stay
          fully clickable — the dim is purely visual. Hands-on steps get a
          lighter dim (they must stay usable); read-only steps get the darker
          classic dim, and a flat dim when there's no target to spotlight. */}
      {spotlight ? (
        <div
          className="absolute rounded-lg ring-2 ring-brand-400 transition-all duration-200 pointer-events-none"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            boxShadow: handsOn ? '0 0 0 9999px rgba(15, 23, 42, 0.38)' : '0 0 0 9999px rgba(15, 23, 42, 0.55)',
          }}
        />
      ) : handsOn ? null : (
        <div className="absolute inset-0 bg-slate-900/55" />
      )}

      {/* Minimized: a compact pill that keeps the page 100% visible. Shows
          detection state so a watched step still reports in. */}
      {minimized ? (
        <button
          onClick={() => setMinimizedPersist(false)}
          className="fixed bottom-4 left-4 z-[61] pointer-events-auto flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-2 text-white shadow-2xl hover:brightness-110 active:scale-95 transition-all"
          aria-label="Expand walkthrough card"
        >
          {step.verifyComplete && !detected
            ? <Radar className="w-3.5 h-3.5 animate-pulse" />
            : <ChevronRight className="w-3.5 h-3.5" />}
          <span className="text-xs font-bold">{stepIndex + 1}/{totalSteps} · {step.title}</span>
        </button>
      ) : (
      <div
        ref={cardRef}
        className={cn(
          'w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden pointer-events-auto',
          cardPos
            ? 'fixed max-h-[70vh] overflow-y-auto'
            : handsOn
              ? 'absolute left-4 bottom-4 max-h-[70vh] overflow-y-auto'
              : cn('absolute', centered && 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'),
        )}
        style={cardPos ? { left: cardPos.x, top: cardPos.y } : handsOn ? undefined : centered ? undefined : cardStyle}
      >
        {/* Header — doubles as the DRAG HANDLE: grab anywhere on it (except
            the buttons) and put the card wherever it isn't in your way. */}
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          className="px-4 py-3 bg-gradient-to-r from-brand-600 to-indigo-600 text-white cursor-move select-none touch-none"
          title="Drag to move this card"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-white/80">
              <GripVertical className="w-3 h-3 text-white/60" />
              {scenario.name}
            </span>
            <span className="flex items-center gap-0.5">
              <button
                onClick={() => setMinimizedPersist(true)}
                aria-label="Minimize walkthrough card"
                title="Minimize — see the whole page"
                className="p-1 rounded hover:bg-white/20 transition-colors"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={exit}
                aria-label="Exit walkthrough"
                className="p-1 rounded hover:bg-white/20 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
          <h3 className="text-sm font-semibold mt-1">{step.title}</h3>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-700 leading-relaxed">{step.body}</p>
          {step.example && (
            <div className="rounded-lg border border-brand-100 bg-brand-50/60 px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-brand-700 mb-0.5">
                <MousePointerClick className="w-3 h-3" /> Try it
              </div>
              <p className="text-[12.5px] text-slate-700 leading-snug">{step.example}</p>
            </div>
          )}
          {step.expect && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-0.5">
                <Eye className="w-3 h-3" /> You should see
              </div>
              <p className="text-[12.5px] text-slate-700 leading-snug">{step.expect}</p>
            </div>
          )}
          {/* Smart-step status: watching for the real action / detected it. */}
          {step.verifyComplete && (
            <div
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-medium',
                detected
                  ? 'border border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border border-brand-200 bg-brand-50/60 text-brand-800',
              )}
            >
              {detected ? (
                <><Check className="w-3.5 h-3.5" /> Detected — nice, moving on…</>
              ) : (
                <><Radar className="w-3.5 h-3.5 animate-pulse" /> Watching the page — do it for real and this step advances itself.</>
              )}
            </div>
          )}
          {/* Flag-note capture: what did you see instead? Quoted in the run report. */}
          {flagNoteOpen && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 mb-1">
                What did you see instead?
              </div>
              <textarea
                autoFocus
                value={flagNote}
                onChange={(e) => setFlagNote(e.target.value)}
                placeholder="e.g. the badge said Pending, not Submitted — lands in the run report"
                className="w-full resize-y rounded-md border border-amber-200 bg-white px-2 py-1.5 text-[12px] leading-snug text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400/50 min-h-[52px]"
              />
              <div className="mt-1.5 flex justify-end gap-2">
                <button
                  onClick={() => { setFlagNoteOpen(false); setFlagNote('') }}
                  className="px-2 py-1 text-[11px] font-medium text-slate-500 rounded hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => recordResult('flag', flagNote)}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <Flag className="w-3 h-3" /> Save flag &amp; continue
                </button>
              </div>
            </div>
          )}

          {/* #109 SELF-SOLVE — "Stuck?" toggle + hybrid help panel (deterministic
              troubleshooting + open the full guide + ask the assistant). */}
          <button
            onClick={() => setHelpOpen((v) => !v)}
            className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-brand-700 transition-colors"
          >
            <LifeBuoy className="w-3 h-3" /> {helpOpen ? 'Hide help' : "Stuck? Explain this step"}
          </button>
          {helpOpen && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 space-y-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                If you're not sure or it didn't match
              </div>
              <ul className="list-disc pl-4 space-y-1 text-[12px] text-slate-700 leading-snug">
                {troubleshootItems.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              <div className="flex flex-wrap gap-2 pt-0.5">
                <button
                  onClick={askAssistant}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-gradient-to-r from-brand-600 to-indigo-600 rounded-lg hover:brightness-110 transition-all"
                >
                  <Sparkles className="w-3 h-3" /> Ask the assistant
                </button>
                <button
                  onClick={openGuide}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-brand-700 border border-brand-200 bg-white rounded-lg hover:bg-brand-50 transition-colors"
                >
                  <BookOpen className="w-3 h-3" /> Open the full guide
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Progress dots */}
        <div className="px-4 pb-2 flex items-center gap-1">
          {scenario.steps.map((s, i) => (
            <div
              key={s.id}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === stepIndex ? 'w-5 bg-brand-500' : i < stepIndex ? 'w-1.5 bg-brand-300' : 'w-1.5 bg-slate-200',
              )}
            />
          ))}
        </div>

        {/* Footer controls */}
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={prev}
              disabled={stepIndex === 0}
              className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              onClick={pause}
              className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 rounded hover:bg-slate-100 transition-colors"
            >
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <span>{stepIndex + 1} / {totalSteps}</span>
            {step.verifyComplete && !detected ? (
              // Smart step: no manual advance — the detection loop owns it.
              // The escape hatch records a flag so the run report is honest
              // about what was never verified.
              <button
                onClick={() => recordResult('flag', 'skipped — auto-detection never confirmed the action')}
                className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
              >
                I'm stuck — skip
              </button>
            ) : step.verifyComplete && detected ? null : step.expect ? (
              // Test-plan step: record whether the expected result matched.
              <>
                <button
                  onClick={() => { setFlagNoteOpen(true); setHelpOpen(true) }}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  <Flag className="w-3.5 h-3.5" /> Didn't match
                </button>
                <button
                  onClick={() => recordResult('pass')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  {isLastStep ? 'It worked, finish' : 'It worked'}
                </button>
              </>
            ) : (
              <button
                onClick={next}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
              >
                {isLastStep ? <Check className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {step.cta ?? (isLastStep ? 'Finish' : 'Done, next step')}
              </button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  )
}
